import React from "react";

import type { JSX } from "react";

import type { GetChannelResponse, Schedule } from "../api/FastAPI.types";
import type { Breadcrumbs, TimelineLoopPreviewMedia } from "../types";

/** TYPE */

export type Action =
  | { type: "SET_ACTIVE_SCHEDULE"; id: string | null }
  | { type: "SET_SCHEDULES"; schedules: Schedule[] }
  | { type: "SET_ITEM_SELECTED"; id: string }
  | { type: "GET_CHANNEL"; data: GetChannelResponse }
  | { type: "SET_ACTIVE_DROP_ZONE"; dropZoneId: null | string }
  | {
      type: "SET_LOOP_TIMELINE_PREVIEW_MEDIA";
      media: TimelineLoopPreviewMedia | null;
    }
  | {
      type: "DELETE_LOOP_TIMELINE_PREVIEW_MEDIA";
    }
  | {
      type: "SET_FAST_HEADER_LAYOUT";
      layout: HeaderLayout;
    }
  | {
      type: "SET_BACKGROUND_LOADING_TIMELINE_ITEMS";
      isLoading: boolean;
    }
  | {
      type: "SET_LOADING_TIMELINE_ITEMS";
      isLoading: boolean;
    }
  | {
      type: "LOCK_SCHEDULE";
      isLocked?: boolean;
      isOwned: boolean;
      lockingAccount?: LockingAccount;
    }
  | {
      type: "RELEASE_SCHEDULE";
    }
  | {
      type: "SET_FILLER_TOOLTIP";
      timestamp: number;
      clickPosition: { x: number; y: number };
    }
  | { type: "CLEAN_FILLER_TOOLTIP" };

type Dispatch = (action: Action) => void;
interface FASTProviderProps {
  children: React.ReactNode;
}

interface ButtonLayoutBase {
  pathname: string;
}

export enum ButtonType {
  SUBMIT = "submit",
  CUSTOM = "custom",
}
export interface SubmitButtonLayout extends ButtonLayoutBase {
  type: ButtonType.SUBMIT;
  formId: string;
}
export interface CustomButtonLayout extends ButtonLayoutBase {
  type: ButtonType.CUSTOM;
  label: string | JSX.Element;
  onClick: () => void;
}

export interface HeaderLayout {
  title: string | JSX.Element;
  button?: JSX.Element;
  breadcrumbs?: Breadcrumbs;
  path?: string;
  lang?: string;
}

interface Layout {
  header?: HeaderLayout;
}

interface LockingAccount {
  id: string;
  firstName: string;
  lastName: string;
  avatar: string;
}

interface State {
  timelineItemSelected?: string;
  timelineScheduleSelected: string | null;
  channelData?: GetChannelResponse;
  schedules: Schedule[];
  activeDropZoneId: null | string;
  layout?: Layout;
  timelineLoopPreviewMedia: TimelineLoopPreviewMedia | null;
  scheduleEditInfo: {
    isReleased: boolean;
    isLocked?: boolean;
    isOwned: boolean;
    lockingAccount?: LockingAccount;
  };
  isLoadingTimelineItems: boolean;
  fillerTooltip?: {
    timestamp: number;
    clickPosition: { x: number; y: number };
  };
}

interface FASTContext {
  state: State;
  dispatch: Dispatch;
}

/** STORE */

const FASTStateContext = React.createContext<FASTContext | undefined>(
  undefined,
);

const initialState: State = {
  timelineItemSelected: undefined,
  channelData: undefined,
  timelineScheduleSelected: null,
  schedules: [],
  activeDropZoneId: null,
  timelineLoopPreviewMedia: null,
  scheduleEditInfo: {
    isReleased: false,
    isLocked: false,
    isOwned: false,
    lockingAccount: undefined,
  },
  isLoadingTimelineItems: false,
  fillerTooltip: undefined,
};

/** REDUCER */

function fastReducer(state: State, action: Action): State {
  const { type } = action;

  switch (type) {
    case "SET_FILLER_TOOLTIP": {
      const { timestamp, clickPosition } = action;

      return {
        ...state,
        fillerTooltip: {
          timestamp,
          clickPosition,
        },
      };
    }
    case "CLEAN_FILLER_TOOLTIP": {
      return {
        ...state,
        fillerTooltip: undefined,
      };
    }
    case "SET_ITEM_SELECTED": {
      const { id } = action;

      return {
        ...state,
        timelineItemSelected: id,
      };
    }
    case "GET_CHANNEL": {
      const { data } = action;
      return {
        ...state,
        channelData: data,
      };
    }
    case "SET_ACTIVE_SCHEDULE": {
      const { id } = action;

      return {
        ...state,
        timelineScheduleSelected: id,
      };
    }
    case "SET_SCHEDULES": {
      const { schedules } = action;

      return {
        ...state,
        schedules,
      };
    }
    case "SET_ACTIVE_DROP_ZONE": {
      const { dropZoneId } = action;

      return {
        ...state,
        activeDropZoneId: dropZoneId,
      };
    }
    case "SET_FAST_HEADER_LAYOUT": {
      const { layout } = action;

      return {
        ...state,
        layout: {
          header: layout,
        },
      };
    }
    case "SET_LOOP_TIMELINE_PREVIEW_MEDIA": {
      const { media } = action;

      return {
        ...state,
        timelineLoopPreviewMedia: media,
      };
    }
    case "DELETE_LOOP_TIMELINE_PREVIEW_MEDIA": {
      return {
        ...state,
        timelineLoopPreviewMedia: null,
      };
    }
    case "SET_LOADING_TIMELINE_ITEMS": {
      const { isLoading } = action;

      return {
        ...state,
        isLoadingTimelineItems: isLoading,
      };
    }
    case "LOCK_SCHEDULE": {
      const { isOwned, isLocked, lockingAccount } = action;

      return {
        ...state,
        scheduleEditInfo: {
          isReleased: false,
          isLocked,
          isOwned,
          lockingAccount,
        },
      };
    }
    case "RELEASE_SCHEDULE": {
      return {
        ...state,
        scheduleEditInfo: {
          isReleased: true,
          isLocked: false,
          isOwned: false,
          lockingAccount: undefined,
        },
      };
    }
    default: {
      throw new Error(`Unhandled action type: ${type}`);
    }
  }
}

/** PROVIDER */

export function FASTProvider({ children }: FASTProviderProps): JSX.Element {
  const [state, dispatch] = React.useReducer(fastReducer, initialState);
  // NOTE: you *might* need to memoize this value
  // Learn more in http://kcd.im/optimize-context

  const value = { state, dispatch }; // eslint-disable-line react/jsx-no-constructed-context-values
  return (
    <FASTStateContext.Provider value={value}>
      {children}
    </FASTStateContext.Provider>
  );
}

/** HOOK */

function useFast(): FASTContext {
  const context = React.useContext(FASTStateContext);
  if (context === undefined) {
    throw new Error("useFast must be used within a FASTProvider");
  }
  return context;
}

export default useFast;
